// ── 3D MODELING FEE ─────────────────────────────────────────────────────
//
// Every visualization proposal carries a modeling fee scaled to the project
// (Jordan's proposal, tiers set by Bernard 2026-10-24). If the client turns
// out to have a complete, usable 3D model at the project planning call, the
// fee is credited back in full against the final payment. All or nothing: a
// partial credit would turn every planning call into a negotiation.
//
// The tier picks itself from the project type and size; the rep can choose a
// different one (a condo deal with three towers on one site is a Large
// Masterplan). Stored on `inputs.modelingFee` as { tier, amount } where tier is
// the rep's pick, null for automatic, or "none" for no fee, and amount is a
// slider-adjusted price (null = the tier's price). Picking a tier resets the
// amount. Quotes saved before the fee existed open as "none", so reopening and
// re-saving one never adds money the client was never shown.
//
// Like the conceptual design fee (./conceptualDesignFee) it is its own printed
// line, never folded into vizNet or the bundle, and it is ADDED to
// oneTimeAllIn / grandTotal and to the visualization split the payment plan
// divides, so the milestones sum to the one-time total with the fee in it.
// The backend mirror is `modeling_fee_of()` in
// backend/services/vexos_payment_plan.py.

export const MODELING_FEE_LABEL = "3D modeling";

export const MODELING_FEE_TIERS = [
  { key: "sfh", label: "SFH", amount: 1000, blurb: "One single-family home and its lot" },
  { key: "building", label: "Building", amount: 2500, blurb: "One multifamily, condo or commercial building" },
  { key: "masterplan", label: "Masterplan", amount: 3500, blurb: "A community: site plan, lots and home models" },
  { key: "large_masterplan", label: "Large Masterplan", amount: 5000, blurb: "A large community or a multi-building site" },
];

// A community at or above this many lots is a Large Masterplan. Same line as
// the calculator's top setup-fee tier (300+), so one size rule reads the same
// everywhere.
export const LARGE_MASTERPLAN_MIN_LOTS = 300;

export const MODELING_FEE_NONE = "none";

// Sales can slide the fee off the tier price (2026-10-26). The ceiling is twice
// the top tier; "None" covers zero.
export const MODELING_FEE_SLIDER_MIN = 500;
export const MODELING_FEE_SLIDER_MAX = 10000;
export const MODELING_FEE_SLIDER_STEP = 250;

export function clampModelingAmount(v) {
  const n = Math.round(num(v) / MODELING_FEE_SLIDER_STEP) * MODELING_FEE_SLIDER_STEP;
  return Math.min(MODELING_FEE_SLIDER_MAX, Math.max(MODELING_FEE_SLIDER_MIN, n));
}

// A pay-in-full client has no later payment to credit, so for them the credit
// is a refund of what they paid for the fee (after the pay-in-full discount).
export const MODELING_FEE_CREDIT_NOTE =
  "Credited back in full if you provide a complete, usable 3D model by the project planning call: off your final payment, or refunded if you paid in full";

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export function modelingFeeTier(key) {
  return MODELING_FEE_TIERS.find((t) => t.key === key) || null;
}

/** The tier the project size implies. mode: "sfh" | "condo" | "community".
 *  A Master Plan quote (a community sold with its included suborbits) is a
 *  Large Masterplan whatever its unit count. */
export function autoModelingTier(mode, { lots = 0, masterPlan = false } = {}) {
  if (mode === "sfh") return "sfh";
  if (mode === "condo") return "building";
  if (masterPlan) return "large_masterplan";
  return num(lots) >= LARGE_MASTERPLAN_MIN_LOTS ? "large_masterplan" : "masterplan";
}

/** The persisted value: { tier, amount } — tier is a known tier, "none", or
 *  null (automatic); amount is the slider price, or null for the tier's. */
export function normalizeModelingFee(raw) {
  const obj = raw && typeof raw === "object" ? raw : {};
  if (obj.tier === MODELING_FEE_NONE) return { tier: MODELING_FEE_NONE, amount: null };
  const hasAmount = obj.amount != null && obj.amount !== "";
  return {
    tier: modelingFeeTier(obj.tier) ? obj.tier : null,
    amount: hasAmount ? clampModelingAmount(obj.amount) : null,
  };
}

/** What a loaded quote starts with: its saved choice, or "none" for a quote
 *  saved before the fee existed. */
export function modelingFeeFromInputs(inputs) {
  if (inputs && inputs.modelingFee && typeof inputs.modelingFee === "object") {
    return normalizeModelingFee(inputs.modelingFee);
  }
  return { tier: MODELING_FEE_NONE };
}

/**
 * The calc with the fee stamped on it and added to its totals. `available`
 * is false on tabs that don't sell visualization (Lot Explorer, a
 * marketing-only quote), where no fee bills.
 */
export function applyModelingFee(calc, fee, { mode, lots, masterPlan = false, available = true } = {}) {
  const autoTier = autoModelingTier(mode, { lots, masterPlan });
  if (!available) {
    return { ...calc, modelingFee: 0, modelingFeeTier: "", modelingFeeTierAuto: autoTier };
  }
  const { tier: picked, amount: adjusted } = normalizeModelingFee(fee);
  if (picked === MODELING_FEE_NONE) {
    return { ...calc, modelingFee: 0, modelingFeeTier: MODELING_FEE_NONE, modelingFeeTierAuto: autoTier };
  }
  const tier = modelingFeeTier(picked || autoTier);
  const amount = adjusted != null ? adjusted : tier.amount;
  return {
    ...calc,
    modelingFee: amount,
    modelingFeeTier: tier.key,
    modelingFeeTierAuto: autoTier,
    modelingFeeTierAmount: tier.amount,
    oneTimeAllIn: num(calc.oneTimeAllIn) + amount,
    grandTotal: num(calc.grandTotal) + amount,
  };
}

/** Sub-line under the fee: the tier, then the credit promise. */
export function modelingFeeSub(tierKey) {
  const tier = modelingFeeTier(tierKey);
  return tier ? `${tier.label} · ${MODELING_FEE_CREDIT_NOTE}` : MODELING_FEE_CREDIT_NOTE;
}
